import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Award, ShieldCheck, Heart, BellPlus, Calendar } from "lucide-react";

const LEVEL = {
  platinum: { color: "#2B1E1A", bg: "linear-gradient(135deg, #D4A359, #B8893F)", label: "Platinum" },
  gold: { color: "#8a6420", bg: "rgba(212,163,89,0.18)", label: "Gold" },
  silver: { color: "#5C5652", bg: "rgba(92,86,82,0.12)", label: "Silver" },
  bronze: { color: "#8a6420", bg: "rgba(212,163,89,0.08)", label: "Bronze" },
  new: { color: "#5C5652", bg: "var(--surface)", label: "New" },
};

export const BuyerScoreCard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/score").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const l = LEVEL[data.level] || LEVEL.new;

  return (
    <div className="p-5 rounded-2xl bg-white border border-[var(--line)] mb-6" data-testid="buyer-score-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)]">Buyer Verification Score</div>
          <div className="font-serif text-3xl text-[var(--teak)] mt-1 flex items-baseline gap-2">
            {data.score}<span className="font-sans text-sm text-[var(--ink-muted)]">/110</span>
          </div>
        </div>
        <div
          className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
          style={{ background: l.bg, color: l.color.includes("gradient") ? "#fff" : l.color }}
        >
          <Award className="w-4 h-4" /> {l.label}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center text-[11px]">
        <div className={`p-2 rounded-lg ${data.factors.phone_verified ? "bg-[rgba(30,67,56,0.08)] text-[var(--emerald)]" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}>
          <ShieldCheck className="w-4 h-4 mx-auto mb-1" />
          <div className="font-semibold">{data.factors.phone_verified}pt</div>
          <div className="opacity-70">Phone</div>
        </div>
        <div className={`p-2 rounded-lg ${data.factors.saved_searches > 0 ? "bg-[rgba(200,90,50,0.08)] text-[var(--primary)]" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}>
          <BellPlus className="w-4 h-4 mx-auto mb-1" />
          <div className="font-semibold">{data.factors.saved_searches}pt</div>
          <div className="opacity-70">{data.saved_searches} saved</div>
        </div>
        <div className={`p-2 rounded-lg ${data.factors.favorites > 0 ? "bg-[rgba(200,90,50,0.08)] text-[var(--primary)]" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}>
          <Heart className="w-4 h-4 mx-auto mb-1" />
          <div className="font-semibold">{data.factors.favorites}pt</div>
          <div className="opacity-70">{data.favorites} saved</div>
        </div>
        <div className={`p-2 rounded-lg ${data.factors.visits_booked > 0 ? "bg-[rgba(30,67,56,0.08)] text-[var(--emerald)]" : "bg-[var(--surface)] text-[var(--ink-muted)]"}`}>
          <Calendar className="w-4 h-4 mx-auto mb-1" />
          <div className="font-semibold">{data.factors.visits_booked + data.factors.visits_confirmed}pt</div>
          <div className="opacity-70">{data.visits_total} visits</div>
        </div>
      </div>
      <p className="text-[11px] text-[var(--ink-muted)] mt-3 italic">
        Agents see this badge next to your inquiries — a higher score means your lead is prioritized.
      </p>
    </div>
  );
};

export const BuyerScoreBadge = ({ level, score }) => {
  const l = LEVEL[level] || LEVEL.new;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: l.bg, color: l.color.includes("gradient") ? "#fff" : l.color }}
      data-testid={`buyer-badge-${level}`}
    >
      <Award className="w-3 h-3" /> {l.label} · {score}
    </span>
  );
};
